import { FC, useState } from 'react';
import { useAsync } from 'react-use';
import { CircularProgress } from '@getgo/chameleon-web-react-wrapper';

import { useAppDispatch, useAppSelector } from 'hooks';
import useProvisioning from 'hooks/useProvisioning';
import { AdyenWrapper, loadAdyenCCCheckout } from 'lib/adyen-client';
import { showErrorSnack } from 'modules/error';
import { accountKey, locale } from 'modules/global-wrapper';
import { isNewTabPayment, sessionInvoiceNumber, sessionSuccessRedirectUrl } from 'modules/session-details';
import Track, { OnePayEventNames, PaymentMethodEventNames } from 'modules/tracking';
import { postTransactionAdyenFinalize, postTransactionAdyenPrepare } from 'modules/transactions-cc';
import { FinalizeAdyenResponse, PrepareAdyenResp } from 'types/transactions-cc';
import {
  COPAS_PAYMENT_STATUS,
  CREDIT_CARD_ADD_PAY,
  PAYMENT_METHODS_TYPES,
  SEARCH_PARAMS,
  TRACKING_EVENT_STATUS,
} from 'utils/constants';
import st from 'utils/shared-translations';

import './adyen-credit-card-form.css';

interface AdyenCreditCardFormProps {
  intent: string;
}

let AdyenClient: AdyenWrapper | undefined = undefined;
let card: any = undefined;

const loadExternalScripts = async (prepareResp: PrepareAdyenResp) => {
  const { adyenJsSettings } = prepareResp;
  AdyenClient = await loadAdyenCCCheckout(adyenJsSettings);
};

const AdyenCreditCardForm: FC<AdyenCreditCardFormProps> = ({ intent }) => {
  const dispatch = useAppDispatch();
  const [isLoading, setLoading] = useState(true);

  const { processQuote, processRenewal, processInvoice } = useProvisioning();

  const selectedGlobalAccountKey = useAppSelector(accountKey);
  const selectedGlobalLocale = useAppSelector(locale);

  const renewalDate = localStorage.getItem(SEARCH_PARAMS.renewalDate) || '';

  // Session details selectors
  const selectedSuccessRedirectUrl = useAppSelector(sessionSuccessRedirectUrl);
  const selectedIsNewTabPayment = useAppSelector(isNewTabPayment);

  const selectedInvoiceNumber = useAppSelector(sessionInvoiceNumber);

  const onPaymentCompleted = async (result, component, prepareResp) => {
    try {
      const response: FinalizeAdyenResponse = await dispatch(
        postTransactionAdyenFinalize({
          checkoutSessionId: prepareResp.adyenSessionId,
          checkoutSessionResult: result.sessionResult,
          setAsDefault: false,
          paymentMethod: 'cca',
        }),
      ).unwrap();
      if (response.status !== COPAS_PAYMENT_STATUS.SUCCESS) {
        throw new Error('Adyen finalization failed');
      } else if (intent === CREDIT_CARD_ADD_PAY) {
        if (selectedIsNewTabPayment) window.close();
        else if (renewalDate) processRenewal(PAYMENT_METHODS_TYPES.cc);
        else if (selectedInvoiceNumber) processInvoice(PAYMENT_METHODS_TYPES.cc);
        else processQuote(PAYMENT_METHODS_TYPES.cc);
      } else {
        window.location.assign(selectedSuccessRedirectUrl);
      }
    } catch {
      Track(PaymentMethodEventNames.creditCardSaveCTA, { status: TRACKING_EVENT_STATUS.copasFailure });
      dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    }
  };

  const onError = (error) => {
    dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    Track(OnePayEventNames.adyenCCFormError, { error });
  };

  const onPaymentFailed = (error) => {
    dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    Track(OnePayEventNames.adyenCCPaymentFailed, { error });
  };

  const initializeAdyenCard = async (prepareResp: PrepareAdyenResp) => {
    const configuration = {
      environment: prepareResp.adyenEnvironment,
      clientKey: prepareResp.adyenClientKey,
      session: {
        id: prepareResp.adyenSessionId,
        sessionData: prepareResp.adyenSessionData,
      },
      onPaymentCompleted: (result, component) => onPaymentCompleted(result, component, prepareResp),
      onError,
      onPaymentFailed,
    };
    try {
      if (AdyenClient) {
        card = await AdyenClient.initialize(configuration);
        setLoading(false);
        card.mount('#adyen-credit-card-form');
      }
    } catch {
      dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    }
  };

  useAsync(async () => {
    if (selectedGlobalAccountKey) {
      try {
        const resp: PrepareAdyenResp = await dispatch(
          postTransactionAdyenPrepare({
            paymentMethodKey: null,
            browserLocale: selectedGlobalLocale,
            returnUrl: window.location.origin,
            paymentMethod: 'cca',
          }),
        ).unwrap();
        await loadExternalScripts(resp);
        initializeAdyenCard(resp);
      } catch {
        Track(PaymentMethodEventNames.prepareCCResponse, { status: TRACKING_EVENT_STATUS.copasFailure });
        dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
      }
    }
  }, [selectedGlobalAccountKey]);

  return (
    <>
      <div id="adyen-credit-card-form"></div>
      {isLoading && (
        <div className="adyen-credit-card-form-loader">
          <CircularProgress />
        </div>
      )}
    </>
  );
};

export default AdyenCreditCardForm;
